import { useCallback, useEffect, useRef, useState } from "react";
import { useSignIn } from "@clerk/clerk-react";
import { KeyRound, ShieldAlert, ShieldCheck, Smartphone, LogOut, Copy, Check } from "lucide-react";
import { useOperator } from "@/context/OperatorContext";
import {
  confirmMfaEnrolment,
  errorMessage,
  setMfaToken,
  startMfaEnrolment,
  verifyMfaCode,
} from "@/api/entities";
import { HudButton } from "@/components/hud";

/**
 * Renders the application only once the operator is fully authenticated.
 * Every other phase gets a dedicated screen instead of the app.
 */
export default function AuthGate({ children }) {
  const { phase } = useOperator();

  switch (phase) {
    case "ready":
      return children;
    case "loading":
      return <Frame title="Authorising" subtitle="Checking your credentials…" />;
    case "signed_out":
      return <SignInScreen />;
    case "denied":
      return <DeniedScreen />;
    case "enrol":
      return <EnrolScreen />;
    case "verify":
      return <VerifyScreen />;
    default:
      return <ErrorScreen />;
  }
}

// ── shared chrome ──────────────────────────────────────────────────────────────

function Frame({ title, subtitle, icon: Icon, tone = "var(--yb-cyan)", children, footer }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="hud-panel w-full max-w-md p-7">
        <div className="flex items-center gap-2.5 mb-1" style={{ color: tone }}>
          {Icon ? <Icon size={18} /> : null}
          <h1 className="font-display text-[17px] tracking-[0.06em]">{title}</h1>
        </div>
        {subtitle && (
          <p className="text-[12.5px] leading-relaxed mb-5" style={{ color: "var(--yb-text-muted)" }}>
            {subtitle}
          </p>
        )}
        {children}
        {footer}
      </div>
    </div>
  );
}

function ErrorNote({ children }) {
  if (!children) return null;
  return (
    <div
      className="mt-3 px-3 py-2 rounded text-[12px]"
      style={{ color: "var(--yb-bad)", border: "1px solid var(--yb-bad)", background: "rgba(255,80,80,0.06)" }}
      role="alert"
    >
      {children}
    </div>
  );
}

/** Six-digit code field shared by the enrol and verify screens. */
function CodeInput({ value, onChange, onSubmit, disabled, label = "Authenticator code" }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <>
      <label
        htmlFor="mfa-code"
        className="font-mono text-[10px] tracking-[0.2em] uppercase"
        style={{ color: "var(--yb-text-muted)" }}
      >
        {label}
      </label>
      <input
        id="mfa-code"
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit?.();
        }}
        disabled={disabled}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={14}
        placeholder="000000"
        className="w-full mt-1.5 h-11 px-3 rounded bg-transparent outline-none font-mono text-[18px] tracking-[0.4em]"
        style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text)" }}
      />
    </>
  );
}

// ── phase: signed out ──────────────────────────────────────────────────────────

function SignInScreen() {
  const { isLoaded, signIn } = useSignIn();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const signInWithGoogle = useCallback(async () => {
    if (!isLoaded) return;
    setBusy(true);
    setErr("");
    try {
      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: "/sso-callback",
        redirectUrlComplete: "/",
      });
    } catch (e) {
      setErr(e?.errors?.[0]?.longMessage || "Could not start Google sign-in.");
      setBusy(false);
    }
  }, [isLoaded, signIn]);

  return (
    <Frame
      title="YABBAI Orchestra"
      subtitle="This console is private. Sign in with an authorised Google account to continue."
      icon={ShieldCheck}
    >
      <HudButton type="button" onClick={signInWithGoogle} disabled={!isLoaded || busy}>
        {busy ? "Redirecting…" : "Continue with Google"}
      </HudButton>
      <ErrorNote>{err}</ErrorNote>
      <p className="mt-5 text-[11px] leading-relaxed" style={{ color: "var(--yb-text-muted)" }}>
        Access is limited to specific accounts and protected by an authenticator app.
        Signing in with any other Google account will be refused.
      </p>
    </Frame>
  );
}

// ── phase: denied ──────────────────────────────────────────────────────────────

function DeniedScreen() {
  const { email, logout } = useOperator();
  return (
    <Frame
      title="Access denied"
      subtitle={
        email
          ? `${email} is not authorised to use this console.`
          : "That account is not authorised to use this console."
      }
      icon={ShieldAlert}
      tone="var(--yb-bad)"
    >
      <HudButton type="button" variant="ghost" tone="cyan" onClick={logout}>
        <LogOut size={14} /> Sign out and try another account
      </HudButton>
    </Frame>
  );
}

// ── phase: error ───────────────────────────────────────────────────────────────

function ErrorScreen() {
  const { error, refresh, logout } = useOperator();
  return (
    <Frame title="Cannot verify access" subtitle={error} icon={ShieldAlert} tone="var(--yb-amber)">
      <div className="flex items-center gap-2">
        <HudButton type="button" onClick={refresh}>
          Retry
        </HudButton>
        <HudButton type="button" variant="ghost" tone="cyan" onClick={logout}>
          Sign out
        </HudButton>
      </div>
    </Frame>
  );
}

// ── phase: enrol (first login) ─────────────────────────────────────────────────

function EnrolScreen() {
  const { email, refresh, logout } = useOperator();
  const [pairing, setPairing] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await startMfaEnrolment();
        if (!cancelled) setPairing(data);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e, "Could not start pairing."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const confirm = async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await confirmMfaEnrolment(code);
      setMfaToken(res.mfa_token);
      setBackupCodes(res.backup_codes);
    } catch (e) {
      setErr(errorMessage(e, "That code didn't match."));
    } finally {
      setBusy(false);
    }
  };

  if (backupCodes) {
    return <BackupCodesScreen codes={backupCodes} onDone={refresh} />;
  }

  return (
    <Frame
      title="Set up two-factor"
      subtitle={`Scan this with Google Authenticator (or any authenticator app), then enter the six-digit code it shows.${email ? ` Pairing ${email}.` : ""}`}
      icon={Smartphone}
    >
      {pairing ? (
        <>
          <div className="rounded p-3 mb-4 flex justify-center" style={{ background: "#fff" }}>
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(pairing.qr_svg)}`}
              alt="QR code for pairing your authenticator app"
              width={190}
              height={190}
            />
          </div>
          <details className="mb-4">
            <summary className="cursor-pointer text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
              Can't scan? Enter this key by hand
            </summary>
            <code
              className="block mt-2 px-2 py-1.5 rounded font-mono text-[12px] break-all"
              style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-dim)" }}
            >
              {pairing.manual_key}
            </code>
          </details>
          <CodeInput value={code} onChange={setCode} onSubmit={confirm} disabled={busy} />
          <ErrorNote>{err}</ErrorNote>
          <div className="flex items-center gap-2 mt-5">
            <HudButton type="button" onClick={confirm} disabled={busy || code.trim().length < 6}>
              {busy ? "Checking…" : "Pair device"}
            </HudButton>
            <HudButton type="button" variant="ghost" tone="cyan" onClick={logout}>
              Sign out
            </HudButton>
          </div>
        </>
      ) : (
        <>
          <p className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>
            Preparing your QR code…
          </p>
          <ErrorNote>{err}</ErrorNote>
        </>
      )}
    </Frame>
  );
}

function BackupCodesScreen({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the codes are on screen to copy manually */
    }
  };
  return (
    <Frame
      title="Save your backup codes"
      subtitle="Each code works once, if you ever lose your phone. This is the only time they will be shown."
      icon={KeyRound}
      tone="var(--yb-amber)"
    >
      <div
        className="grid grid-cols-2 gap-1.5 p-3 rounded font-mono text-[13px]"
        style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text)" }}
      >
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-5">
        <HudButton type="button" onClick={onDone}>
          I've saved them — continue
        </HudButton>
        <HudButton type="button" variant="ghost" tone="cyan" onClick={copy}>
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
        </HudButton>
      </div>
    </Frame>
  );
}

// ── phase: verify (returning session) ──────────────────────────────────────────

function VerifyScreen() {
  const { email, refresh, logout } = useOperator();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await verifyMfaCode(code);
      setMfaToken(res.mfa_token);
      await refresh();
    } catch (e) {
      setErr(errorMessage(e, "That code didn't match."));
      setBusy(false);
    }
  };

  return (
    <Frame
      title="Two-factor check"
      subtitle={`Enter the six-digit code from your authenticator app${email ? ` for ${email}` : ""}. A backup code works too.`}
      icon={ShieldCheck}
    >
      <CodeInput value={code} onChange={setCode} onSubmit={submit} disabled={busy} />
      <ErrorNote>{err}</ErrorNote>
      <div className="flex items-center gap-2 mt-5">
        <HudButton type="button" onClick={submit} disabled={busy || code.trim().length < 6}>
          {busy ? "Verifying…" : "Unlock console"}
        </HudButton>
        <HudButton type="button" variant="ghost" tone="cyan" onClick={logout}>
          Sign out
        </HudButton>
      </div>
    </Frame>
  );
}
